import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WaveIcon } from '../icons/SvgIcons';
import {
  Container,
  Typography,
  Button,
  Chip,
  Grid,
  Paper,
  Box,
  Stack,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Badge,
  Divider,
  Tabs,
  Tab,
  Tooltip,
  Skeleton,
  Dialog,
  DialogContent,
  DialogTitle,
  Menu,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
} from '@mui/material';
import {
  PersonOutline,
  Phone,
  AccessTime,
  Refresh,
  VideoCall,
  CheckCircle,
  Warning,
  PlayArrow,
  Groups,
  ExitToApp,
  PhoneInTalk,
  History,
  PlayCircleOutline,
  SummarizeOutlined,
  LiveTv,
  SmartToy,
  FiberManualRecord,
  PeopleAlt,
  Close,
  VideoLibrary,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useCallStore } from '../../stores/callStore';
import { useAuthStore } from '../../stores/authStore';
import { useQueueUpdates, useNotifications } from '../../hooks/useWebSocket';
import { LiveCallsList } from '../calls/LiveCallsList';
import { apiRequest } from '../../utils/api';
import { MobileBottomNav } from '../common/MobileBottomNav';

export const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { 
    queueEntries, 
    activeCalls, 
    queueLoading, 
    queueError,
    loadQueue, 
    loadActiveCalls, 
    assignNextCall, 
    assignSpecificCall,
    getStats 
  } = useCallStore();

  const { isConnected } = useQueueUpdates();
  const { notifications } = useNotifications();

  const [selectedPriority, setSelectedPriority] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [aiAgentLoading, setAiAgentLoading] = useState({});

  useEffect(() => {
    loadQueue();
    loadActiveCalls();
  }, [loadQueue, loadActiveCalls]);

  const handleNextCall = async () => {
    setRefreshing(true);
    try {
      const result = await assignNextCall();
      if (result.success) {
        navigate(`/video-call/${result.callId}`);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error starting next call:', error);
      alert('Error al asignar la siguiente llamada');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSpecificCall = async (entryId) => {
    try {
      const result = await assignSpecificCall(entryId);
      if (result.success) {
        navigate(`/video-call/${result.callId}`);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error starting specific call:', error);
      alert('Error al asignar la llamada');
    }
  };

  const handleStartCallWithAI = async (entryId) => {
    setAiAgentLoading(prev => ({ ...prev, [entryId]: true }));
    try {
      const result = await assignSpecificCall(entryId);
      if (!result.success) {
        alert(`Error: ${result.error}`);
        return;
      }

      const response = await apiRequest(`/calls/${result.callId}/ai-agent/start`, {
        method: 'POST',
        headers: {
          'X-User-ID': user.id,
          'X-User-Role': user.role,
        }
      });
      
      if (response.status === 'started' || response.status === 'already_running') {
        alert('Agente de IA iniciado. El bot está atendiendo al paciente.');
        await loadActiveCalls();
      } else {
        alert('Error al iniciar el agente de IA');
      }
    } catch (error) {
      console.error('Error starting call with AI:', error);
      alert('Error al iniciar la llamada con IA');
    } finally {
      setAiAgentLoading(prev => ({ ...prev, [entryId]: false }));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadQueue(), loadActiveCalls()]);
    setRefreshing(false);
  };

  const filteredQueue = selectedPriority === 'all' 
    ? queueEntries 
    : queueEntries.filter(entry => entry.priority === parseInt(selectedPriority));

  const stats = getStats();

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1: return 'Urgente';
      case 2: return 'Normal';
      case 3: return 'Rutina';
      default: return 'Normal';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'error';
      case 2: return 'warning';
      case 3: return 'info';
      default: return 'default';
    }
  };

  const formatWaitTime = (waitingSince) => {
    const minutes = Math.floor((Date.now() - new Date(waitingSince).getTime()) / 60000);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Top Navigation Bar */}
      <Box
        component="header"
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 30,
        }}
      >
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="h5"
                onClick={() => navigate('/')}
                sx={{
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.03em',
                }}
              >
                Vincula
              </Typography>
              <Chip
                icon={<FiberManualRecord sx={{ fontSize: 8 }} />}
                label={isConnected ? 'Conectado' : 'Desconectado'}
                size="small"
                color={isConnected ? 'success' : 'default'}
                variant="outlined"
                sx={{ height: 26, '& .MuiChip-icon': { ml: 0.5 } }}
              />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PeopleAlt sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/dashboard/employee/patients')}
                sx={{ fontSize: '0.8rem', py: 0.75 }}
              >
                Pacientes
              </Button>
              {user?.role === 'admin' && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AdminPanelSettings sx={{ fontSize: 18 }} />}
                  onClick={() => navigate('/admin/users')}
                  sx={{ fontSize: '0.8rem', py: 0.75 }}
                >
                  Usuarios
                </Button>
              )}
              {user?.role === 'admin' && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<PhoneInTalk sx={{ fontSize: 18 }} />}
                  onClick={() => navigate('/admin/control-room')}
                  sx={{ fontSize: '0.8rem', py: 0.75 }}
                >
                  Control
                </Button>
              )}
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#EEF2FF', color: '#4F46E5', fontSize: '0.8rem' }}>
                  {(user?.first_name?.[0] || 'A').toUpperCase()}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="body2" fontWeight={600} color="text.primary" lineHeight={1.2}>
                    {user?.first_name} {user?.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" lineHeight={1}>
                    Acompañante
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={logout}
                startIcon={<ExitToApp sx={{ fontSize: 18 }} />}
                sx={{ fontSize: '0.8rem', py: 0.75 }}
              >
                Salir
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Welcome Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Buen día, {user?.first_name || 'Acompañante'} <WaveIcon width={28} height={28} color="#2563EB" />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiona las solicitudes de acompañamiento y sesiones activas.
          </Typography>
        </Box>

        {/* Quick Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            {
              label: 'En cola',
              value: stats.queueLength,
              color: '#2563EB',
              bg: '#EFF6FF',
              icon: <PhoneInTalk sx={{ fontSize: 20 }} />,
            },
            {
              label: 'Urgentes',
              value: stats.urgentCalls,
              color: '#DC2626',
              bg: '#FEF2F2',
              icon: <Warning sx={{ fontSize: 20 }} />,
            },
            {
              label: 'Activas',
              value: stats.activeCallsCount,
              color: '#059669',
              bg: '#F0FDF4',
              icon: <VideoCall sx={{ fontSize: 20 }} />,
            },
          ].map((item) => (
            <Grid item xs={4} key={item.label}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Avatar sx={{ bgcolor: item.bg, color: item.color, width: 40, height: 40 }}>
                  {item.icon}
                </Avatar>
                <Box>
                  <Typography variant="h3" sx={{ color: item.color, lineHeight: 1 }}>
                    {item.value}
                  </Typography>
                  <Typography variant="caption">{item.label}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Notifications */}
        {notifications.length > 0 && (
          <Stack spacing={1} sx={{ mb: 3 }}>
            {notifications.slice(0, 2).map(notification => (
              <Alert key={notification.id} severity={notification.type || 'info'} sx={{ py: 0.5 }}>
                {notification.message}
              </Alert>
            ))}
          </Stack>
        )}

        {/* Main Tabs */}
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => setSelectedTab(newValue)}
              sx={{ px: 3, minHeight: 48 }}
            >
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PhoneInTalk sx={{ fontSize: 18 }} />
                    <span>Solicitudes</span>
                    {stats.queueLength > 0 && (
                      <Badge badgeContent={stats.queueLength} color="primary" sx={{ ml: 1 }} />
                    )}
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LiveTv sx={{ fontSize: 18 }} />
                    <span>Sesiones en vivo</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
              <Tab
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <History sx={{ fontSize: 18 }} />
                    <span>Grabaciones</span>
                  </Stack>
                }
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#FAFBFC' }}>
            {/* Tab 1: Queue */}
            {selectedTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                  {/* Queue Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                    <Typography variant="h4">Cola de solicitudes</Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={selectedPriority}
                          onChange={(e) => setSelectedPriority(e.target.value)}
                          displayEmpty
                          sx={{ fontSize: '0.8125rem' }}
                        >
                          <MenuItem value="all">Todas</MenuItem>
                          <MenuItem value="1">Urgente</MenuItem>
                          <MenuItem value="2">Normal</MenuItem>
                          <MenuItem value="3">Rutina</MenuItem>
                        </Select>
                      </FormControl>
                      <Tooltip title="Actualizar">
                        <IconButton onClick={handleRefresh} disabled={refreshing} size="small">
                          {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleNextCall}
                        disabled={queueEntries.length === 0 || refreshing}
                        startIcon={<VideoCall sx={{ fontSize: 18 }} />}
                      >
                        Siguiente
                      </Button>
                    </Stack>
                  </Stack>

                  {queueError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Error al cargar la cola: {queueError}
                      <Button onClick={handleRefresh} size="small" sx={{ ml: 2 }}>Reintentar</Button>
                    </Alert>
                  )}

                  {/* Queue List */}
                  {queueLoading ? (
                    <Stack spacing={1.5}>
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={88} sx={{ borderRadius: 2 }} />
                      ))}
                    </Stack>
                  ) : filteredQueue.length > 0 ? (
                    <Stack spacing={1.5}>
                      <AnimatePresence>
                        {filteredQueue.map((entry, index) => (
                          <motion.div
                            key={entry.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                border: '1px solid',
                                borderColor: entry.priority === 1 ? 'error.main' : 'divider',
                                borderLeftWidth: 3,
                                borderLeftColor: entry.priority === 1 ? 'error.main' : entry.priority === 2 ? 'warning.main' : 'info.main',
                                bgcolor: 'white',
                                transition: 'border-color 0.15s, box-shadow 0.15s',
                                '&:hover': {
                                  borderColor: '#CBD5E1',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                },
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                                  <Avatar
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      bgcolor: entry.priority === 1 ? '#FEE2E2' : '#EFF6FF',
                                      color: entry.priority === 1 ? '#DC2626' : '#2563EB',
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    <PersonOutline sx={{ fontSize: 20 }} />
                                  </Avatar>
                                  <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Typography variant="body1" fontWeight={600} color="text.primary">
                                        {entry.patientName || `Paciente #${entry.id}`}
                                      </Typography>
                                      <Chip
                                        label={getPriorityLabel(entry.priority)}
                                        color={getPriorityColor(entry.priority)}
                                        size="small"
                                        sx={{ height: 22, fontSize: '0.7rem' }}
                                      />
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.25 }}>
                                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AccessTime sx={{ fontSize: 13 }} /> {formatWaitTime(entry.joinedAt)}
                                      </Typography>
                                      {entry.reason && (
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                          {entry.reason}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Box>
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<Phone sx={{ fontSize: 16 }} />}
                                    onClick={() => handleSpecificCall(entry.id)}
                                    sx={{ fontSize: '0.8rem', py: 0.75 }}
                                  >
                                    Atender
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={aiAgentLoading[entry.id] ? <CircularProgress size={14} /> : <SmartToy sx={{ fontSize: 16 }} />}
                                    onClick={() => handleStartCallWithAI(entry.id)}
                                    disabled={aiAgentLoading[entry.id]}
                                    sx={{ fontSize: '0.8rem', py: 0.75, borderColor: '#D1FAE5', color: '#059669', '&:hover': { borderColor: '#10B981', bgcolor: '#F0FDF4' } }}
                                  >
                                    IA
                                  </Button>
                                </Stack>
                              </Stack>
                            </Paper>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </Stack>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        textAlign: 'center',
                        py: 8,
                        border: '1px dashed',
                        borderColor: '#E2E8F0',
                        bgcolor: 'white',
                      }}
                    >
                      <PhoneInTalk sx={{ fontSize: 48, color: '#CBD5E1', mb: 1.5 }} />
                      <Typography variant="h5" color="text.secondary" fontWeight={500} gutterBottom>
                        Sin solicitudes pendientes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Las nuevas solicitudes aparecerán aquí automáticamente
                      </Typography>
                    </Paper>
                  )}
                </Grid>

                {/* Active Sessions Sidebar */}
                <Grid item xs={12} lg={4}>
                  <Typography variant="h4" sx={{ mb: 2.5 }}>
                    Sesiones activas
                    {activeCalls.length > 0 && (
                      <Chip label={activeCalls.length} size="small" color="success" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Typography>

                  {activeCalls.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        textAlign: 'center',
                        py: 5,
                        border: '1px dashed',
                        borderColor: '#E2E8F0',
                        bgcolor: 'white',
                      }}
                    >
                      <Groups sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No hay sesiones activas
                      </Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={1.5}>
                      {activeCalls.map((call) => (
                        <Paper
                          key={call.id}
                          elevation={0}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'white',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: '#10B981', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                          }}
                          onClick={() => navigate(`/video-call/${call.id}`)}
                        >
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar sx={{ bgcolor: '#D1FAE5', color: '#059669', width: 36, height: 36 }}>
                              <VideoCall sx={{ fontSize: 18 }} />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600} color="text.primary">
                                {call.patientName || `Sesión ${call.id}`}
                              </Typography>
                              <Typography variant="caption">
                                {call.employeeName} · {formatWaitTime(call.startedAt)}
                              </Typography>
                            </Box>
                            <Button size="small" startIcon={<PlayArrow sx={{ fontSize: 16 }} />} sx={{ fontSize: '0.75rem' }}>
                              Unirse
                            </Button>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Tab 2: Live Calls Monitor */}
            {selectedTab === 1 && (
              <Box>
                <Typography variant="h4" sx={{ mb: 1 }}>Monitor de sesiones</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Supervisa todas las videollamadas activas en el sistema.
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <LiveCallsList userRole="employee" />
              </Box>
            )}

            {/* Tab 3: Recordings */}
            {selectedTab === 2 && (
              <RecordingsPanel />
            )}
          </Box>
        </Paper>

        {/* Padding bottom for mobile navigation */}
        <Box sx={{ height: { xs: 72, md: 0 } }} />
      </Container>

      <MobileBottomNav />
    </Box>
  );
};

// --- Recordings Panel with inline video player + summaries + transcripts ---
const RecordingsPanel = () => {
  const { user } = useAuthStore();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [playerUrl, setPlayerUrl] = useState('');
  const [playerTitle, setPlayerTitle] = useState('');
  const [loadingId, setLoadingId] = useState(null);

  // Notes dialog (summary + transcript)
  const [notesDialog, setNotesDialog] = useState({ open: false, callId: null, title: '', loading: false, notes: [] });
  const [notesTab, setNotesTab] = useState(0);
  // Actions menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRec, setMenuRec] = useState(null);

  useEffect(() => {
    fetchRecordings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecordings = async () => {
    try {
      const data = await apiRequest('/calls/recordings');
      setRecordings(data.recordings || []);
    } catch (error) {
      console.error('Error fetching recordings:', error);
    } finally {
      setLoading(false);
    }
  };

  const playRecording = async (recording) => {
    setLoadingId(recording.id);
    try {
      const data = await apiRequest(`/calls/${recording.call_id}/recordings/${recording.id}/url`);
      if (data.url) {
        setPlayerUrl(data.url);
        setPlayerTitle(
          `${recording.patient_name || 'Paciente'} — ${new Date(recording.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
        );
        setPlayerOpen(true);
      }
    } catch (error) {
      console.error('Error fetching presigned URL:', error);
      alert('Error al obtener la grabación.');
    } finally {
      setLoadingId(null);
    }
  };

  const closePlayer = () => {
    setPlayerOpen(false);
    setPlayerUrl('');
  };

  const openNotes = async (recording, defaultTab = 0) => {
    setNotesTab(defaultTab);
    setNotesDialog({
      open: true,
      callId: recording.call_id,
      title: `${recording.patient_name || 'Paciente'} — ${new Date(recording.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}`,
      loading: true,
      notes: [],
    });
    try {
      const data = await apiRequest(`/calls/${recording.call_id}/notes`);
      setNotesDialog(prev => ({ ...prev, loading: false, notes: data.notes || [] }));
    } catch (error) {
      console.error('Error fetching notes:', error);
      setNotesDialog(prev => ({ ...prev, loading: false, notes: [] }));
    }
  };

  const getSummary = (notes) => {
    const s = notes.find(n => n.note_type === 'ai_generated_summary') || notes.find(n => n.note_type === 'ai_summary');
    return s?.content || null;
  };

  const getTranscript = (notes) => {
    const t = notes.find(n => n.note_type === 'ai_transcript');
    return t?.content || null;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    const mb = bytes / (1024 * 1024);
    return mb < 1024 ? `${mb.toFixed(1)} MB` : `${(mb / 1024).toFixed(2)} GB`;
  };

  const reasonLabel = (reason) => {
    const map = { ai_direct_call: 'Llamada IA', family_call: 'Familiar', '': 'Consulta' };
    return map[reason] || reason || 'Consulta';
  };

  if (loading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={80} />
        ))}
      </Stack>
    );
  }

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>Grabaciones</Typography>
          <Typography variant="body2" color="text.secondary">
            {recordings.length} grabación{recordings.length !== 1 ? 'es' : ''} disponible{recordings.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<Refresh sx={{ fontSize: 16 }} />}
          onClick={() => { setLoading(true); fetchRecordings(); }}
        >
          Actualizar
        </Button>
      </Stack>

      {recordings.length === 0 ? (
        <Paper elevation={0} sx={{ textAlign: 'center', py: 8, border: '1px dashed', borderColor: '#E2E8F0', bgcolor: 'white' }}>
          <VideoLibrary sx={{ fontSize: 48, color: '#CBD5E1', mb: 1.5 }} />
          <Typography variant="h5" color="text.secondary" fontWeight={500} gutterBottom>
            No hay grabaciones disponibles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Las grabaciones aparecerán aquí cuando se completen sesiones
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {recordings.map((rec) => (
            <Paper
              key={rec.id}
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: '#93C5FD' },
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Avatar sx={{ bgcolor: '#EFF6FF', color: '#2563EB', width: 40, height: 40 }}>
                  <PlayCircleOutline sx={{ fontSize: 22 }} />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.3 }}>
                    <Typography variant="body1" fontWeight={600} noWrap>
                      {rec.patient_name || 'Paciente'}
                    </Typography>
                    <Chip
                      label={reasonLabel(rec.reason)}
                      size="small"
                      sx={{ height: 20, fontSize: '0.65rem', bgcolor: rec.reason === 'ai_direct_call' ? '#F0FDF4' : '#EFF6FF', color: rec.reason === 'ai_direct_call' ? '#059669' : '#2563EB' }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(rec.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Duración: {formatDuration(rec.duration_seconds)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                      {formatFileSize(rec.recording_size_bytes)}
                    </Typography>
                  </Stack>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRec(rec); }}
                  sx={{ fontSize: '0.75rem', minWidth: 110 }}
                >
                  Ver detalles
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Inline Video Player Dialog */}
      <Dialog
        open={playerOpen}
        onClose={closePlayer}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#000', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2 }}>
          <Typography variant="body2" color="grey.300" noWrap sx={{ flex: 1 }}>
            {playerTitle}
          </Typography>
          <IconButton onClick={closePlayer} size="small" sx={{ color: 'grey.400' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
          {playerUrl && (
            <video
              src={playerUrl}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '70vh', outline: 'none' }}
              controlsList="nodownload"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Notes Dialog (Summary + Transcript) */}
      <Dialog
        open={notesDialog.open}
        onClose={() => setNotesDialog({ open: false, callId: null, title: '', loading: false, notes: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
          <Typography variant="h6" noWrap sx={{ flex: 1 }}>
            {notesDialog.title}
          </Typography>
          <IconButton onClick={() => setNotesDialog({ open: false, callId: null, title: '', loading: false, notes: [] })} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {notesDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Tabs value={notesTab} onChange={(_, v) => setNotesTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Resumen" sx={{ textTransform: 'none' }} />
                <Tab label="Transcripción" sx={{ textTransform: 'none' }} />
              </Tabs>
              {notesTab === 0 && (
                <Box>
                  {getSummary(notesDialog.notes) ? (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                      {getSummary(notesDialog.notes)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No hay resumen disponible para esta llamada.
                    </Typography>
                  )}
                </Box>
              )}
              {notesTab === 1 && (
                <Box>
                  {getTranscript(notesDialog.notes) ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        maxHeight: '60vh',
                        overflow: 'auto',
                        bgcolor: '#F8FAFC',
                        border: '1px solid',
                        borderColor: 'divider',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        lineHeight: 1.8,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {getTranscript(notesDialog.notes)}
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No hay transcripción disponible para esta llamada.
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Actions Dropdown Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuRec(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 190, mt: 0.5 } }}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); if (menuRec) playRecording(menuRec); }}>
          <MuiListItemIcon><PlayArrow sx={{ fontSize: 18 }} /></MuiListItemIcon>
          <MuiListItemText>Ver grabación</MuiListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); if (menuRec) openNotes(menuRec, 0); }}>
          <MuiListItemIcon><SummarizeOutlined sx={{ fontSize: 18 }} /></MuiListItemIcon>
          <MuiListItemText>Ver resumen</MuiListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); if (menuRec) openNotes(menuRec, 1); }}>
          <MuiListItemIcon><History sx={{ fontSize: 18 }} /></MuiListItemIcon>
          <MuiListItemText>Ver transcripción</MuiListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
